import { Controller } from "@hotwired/stimulus"

/**
 * Admin Sidebar Controller - Collapsible sidebar with localStorage persistence
 *
 * State management: collapsedValueChanged() is the single source of truth for
 * applying collapsed/expanded state. All other code paths only set the value.
 */
export default class extends Controller {
  static targets = ["sidebar", "toggle", "title", "label", "adminInfo", "groupLabel", "badge", "badgeDot"]
  static values = {
    collapsed: { type: Boolean, default: false }
  }

  connect() {
    const savedState = localStorage.getItem("admin-sidebar-collapsed")
    if (savedState !== null) {
      this.collapsedValue = savedState === "true"
    }
    // If saved state equals default (false), collapsedValueChanged won't fire.
    // Apply initial state explicitly in that case.
    if (savedState === null || savedState === "false") {
      this.applyState()
    }

    this._boundHandleKeyboard = this.handleKeyboard.bind(this)
    document.addEventListener("keydown", this._boundHandleKeyboard)
  }

  disconnect() {
    document.removeEventListener("keydown", this._boundHandleKeyboard)
  }

  toggle(event) {
    event?.preventDefault()
    this.collapsedValue = !this.collapsedValue
    // applyState() handled by collapsedValueChanged callback
    this.saveState()
  }

  // Single source of truth for state application
  collapsedValueChanged() {
    this.applyState()
  }

  applyState() {
    if (this.collapsedValue) {
      this.collapse()
    } else {
      this.expand()
    }
  }

  collapse() {
    if (this.hasSidebarTarget) {
      this.sidebarTarget.classList.add("w-14")
      this.sidebarTarget.classList.remove("w-60")
    }

    if (this.hasTitleTarget) {
      this.titleTarget.classList.add("hidden")
    }

    this.labelTargets.forEach(label => {
      label.classList.add("hidden")
    })

    if (this.hasAdminInfoTarget) {
      this.adminInfoTarget.classList.add("hidden")
    }

    this.groupLabelTargets.forEach(groupLabel => {
      groupLabel.classList.add("hidden")
    })

    this.badgeTargets.forEach(badge => {
      badge.classList.add("hidden")
    })

    this.badgeDotTargets.forEach(dot => {
      dot.classList.remove("hidden")
    })

    this.dispatch("collapsed", { detail: { collapsed: true } })
  }

  expand() {
    if (this.hasSidebarTarget) {
      this.sidebarTarget.classList.add("w-60")
      this.sidebarTarget.classList.remove("w-14")
    }

    if (this.hasTitleTarget) {
      this.titleTarget.classList.remove("hidden")
    }

    this.labelTargets.forEach(label => {
      label.classList.remove("hidden")
    })

    if (this.hasAdminInfoTarget) {
      this.adminInfoTarget.classList.remove("hidden")
    }

    this.groupLabelTargets.forEach(groupLabel => {
      groupLabel.classList.remove("hidden")
    })

    this.badgeTargets.forEach(badge => {
      badge.classList.remove("hidden")
    })

    this.badgeDotTargets.forEach(dot => {
      dot.classList.add("hidden")
    })

    this.dispatch("expanded", { detail: { collapsed: false } })
  }

  saveState() {
    localStorage.setItem("admin-sidebar-collapsed", this.collapsedValue.toString())
  }

  handleKeyboard(event) {
    if ((event.metaKey || event.ctrlKey) && event.key === "b") {
      event.preventDefault()
      this.toggle()
    }
  }

  dispatchCommandOpen() {
    window.dispatchEvent(new Event("admin-command:open"))
  }
}
